import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { ChevronLeft, ChevronRight, Loader2, Search, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { api, friendlyError, unwrap } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { PROFILE_TYPES, ROUTES, ROLE_LABELS } from '@/lib/constants';
import { ADMIN } from '@/constants/testIds';

const STATUS_OPTIONS = [
  { value: 'all', label: 'Todos los estados' },
  { value: 'active', label: 'Activa' },
  { value: 'pending_email_verification', label: 'Pendiente email' },
  { value: 'pending_guardian_consent', label: 'Pendiente tutor' },
  { value: 'suspended', label: 'Suspendida' },
  { value: 'deleted', label: 'Eliminada' },
];

export default function AdminUsersList() {
  const { user: currentUser } = useAuth();
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [profileFilter, setProfileFilter] = useState('all');

  const [data, setData] = useState({ items: [], page: 1, page_size: 10, total: 0, total_pages: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, q, statusFilter, profileFilter]);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const params = { page, page_size: pageSize };
      if (q.trim()) params.q = q.trim();
      if (statusFilter !== 'all') params.status = statusFilter;
      if (profileFilter !== 'all') params.profile_type = profileFilter;
      const res = await api.get('/admin/users/', { params });
      setData(unwrap(res));
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setLoading(false);
    }
  }

  async function handleDelete() {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/users/${deleteTarget.id}`);
      toast.success('Usuario eliminado.');
      setDeleteTarget(null);
      await load();
    } catch (err) {
      toast.error(friendlyError(err));
    } finally {
      setDeleting(false);
    }
  }

  return (
    <AdminLayout>
      <section>
        <div className="mb-6">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <div>
              <span className="text-[11px] uppercase tracking-[0.25em] text-[var(--ns-gold-600)] font-semibold">
                Administración
              </span>
              <h1 className="font-display uppercase tracking-tight text-[var(--ns-text)] text-3xl sm:text-5xl mt-1 leading-[1.02]">
                Usuarios
              </h1>
            </div>
            <p className="text-sm text-[var(--ns-text-muted)]">
              {data.total} resultado{data.total === 1 ? '' : 's'}
            </p>
          </div>
        </div>

          {/* Filtros */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--ns-text-muted)]" />
              <Input
                placeholder="Buscar por nombre o email…"
                value={q}
                onChange={(e) => { setQ(e.target.value); setPage(1); }}
                data-testid={ADMIN.searchInput}
                className="h-11 bg-white pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
              <SelectTrigger data-testid={ADMIN.statusFilter} className="h-11 bg-white">
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={profileFilter} onValueChange={(v) => { setProfileFilter(v); setPage(1); }}>
              <SelectTrigger data-testid={ADMIN.profileTypeFilter} className="h-11 bg-white">
                <SelectValue placeholder="Tipo de perfil" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los perfiles</SelectItem>
                {PROFILE_TYPES.map((p) => (
                  <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Tabla */}
          <Card className="bg-white border border-[var(--ns-border)] rounded-2xl shadow-[var(--ns-shadow-soft)] overflow-hidden">
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center py-16">
                  <Loader2 className="h-7 w-7 animate-spin text-[var(--ns-gold-600)]" />
                </div>
              ) : error ? (
                <p className="p-6 text-sm text-[var(--ns-red)]">{error}</p>
              ) : data.items.length === 0 ? (
                <p className="p-8 text-center text-sm text-[var(--ns-text-muted)]">
                  No hay usuarios que coincidan con la búsqueda.
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table
                    data-testid={ADMIN.usersTable}
                    className="w-full text-sm"
                  >
                    <thead className="bg-[var(--ns-bg-2)] text-[11px] uppercase tracking-widest text-[var(--ns-text-muted)]">
                      <tr>
                        <th className="text-left px-4 py-3 font-semibold">Nombre</th>
                        <th className="text-left px-4 py-3 font-semibold">Email</th>
                        <th className="text-left px-4 py-3 font-semibold hidden md:table-cell">Perfil</th>
                        <th className="text-left px-4 py-3 font-semibold hidden lg:table-cell">Rol</th>
                        <th className="text-left px-4 py-3 font-semibold">Estado</th>
                        <th className="px-4 py-3" />
                      </tr>
                    </thead>
                    <tbody>
                      {data.items.map((u) => {
                        const profile = PROFILE_TYPES.find((p) => p.value === u.profile_type);
                        return (
                          <tr
                            key={u.id}
                            data-testid={ADMIN.userRow(u.id)}
                            className="border-t border-[var(--ns-border)] hover:bg-[var(--ns-amber-50)]/40"
                          >
                            <td className="px-4 py-3 font-semibold text-[var(--ns-text)]">
                              {u.first_name} {u.last_name}
                            </td>
                            <td className="px-4 py-3 text-[var(--ns-text-muted)] break-all">{u.email}</td>
                            <td className="px-4 py-3 hidden md:table-cell text-[var(--ns-text-muted)]">
                              {profile?.label || u.profile_type}
                            </td>
                            <td className="px-4 py-3 hidden lg:table-cell text-[var(--ns-text-muted)]">
                              {ROLE_LABELS[u.role] || u.role}
                            </td>
                            <td className="px-4 py-3"><StatusBadge status={u.account_status} /></td>
                            <td className="px-4 py-3 text-right">
                              <div className="inline-flex items-center justify-end gap-1.5">
                                <Button
                                  asChild
                                  variant="outline"
                                  size="sm"
                                  data-testid={ADMIN.userRowOpen(u.id)}
                                  className="rounded-lg bg-white border-[var(--ns-border-strong)] text-[var(--ns-text)] hover:bg-[var(--ns-amber-50)] hover:border-[var(--ns-gold)]"
                                >
                                  <Link to={ROUTES.adminUserDetail(u.id)}>Abrir</Link>
                                </Button>
                                {currentUser?.id !== u.id ? (
                                  <Button
                                    variant="ghost" size="sm"
                                    onClick={() => setDeleteTarget(u)}
                                    className="rounded-lg h-9 px-2 text-red-600 hover:bg-red-50 hover:text-red-700"
                                    data-testid={`admin-users-delete-${u.id}`}
                                    title="Eliminar usuario"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                ) : null}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Paginación */}
          {data.total_pages > 1 && (
            <div className="mt-5 flex items-center justify-between">
              <Button
                variant="outline"
                disabled={data.page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                data-testid={ADMIN.prevPage}
                className="rounded-lg bg-white border-[var(--ns-border-strong)] text-[var(--ns-text)] hover:bg-[var(--ns-amber-50)] disabled:opacity-50"
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Anterior
              </Button>
              <span className="text-sm text-[var(--ns-text-muted)]">
                Página <strong className="text-[var(--ns-text)]">{data.page}</strong> de {data.total_pages}
              </span>
              <Button
                variant="outline"
                disabled={data.page >= data.total_pages}
                onClick={() => setPage((p) => p + 1)}
                data-testid={ADMIN.nextPage}
                className="rounded-lg bg-white border-[var(--ns-border-strong)] text-[var(--ns-text)] hover:bg-[var(--ns-amber-50)] disabled:opacity-50"
              >
                Siguiente
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          )}

        <AlertDialog open={!!deleteTarget} onOpenChange={(v) => { if (!v) setDeleteTarget(null); }}>
          <AlertDialogContent data-testid="admin-users-delete-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>¿Eliminar este usuario?</AlertDialogTitle>
              <AlertDialogDescription>
                Vas a eliminar a <strong>{deleteTarget ? `${deleteTarget.first_name || ''} ${deleteTarget.last_name || ''}`.trim() : ''}</strong>
                {deleteTarget?.email ? ` (${deleteTarget.email})` : ''}.
                {' '}La cuenta quedará marcada como eliminada y cualquier sesión activa será invalidada.
                {' '}La acción es reversible mediante soporte (eliminación lógica).
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="admin-users-delete-cancel">Cancelar</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                disabled={deleting}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="admin-users-delete-confirm"
              >
                {deleting ? 'Eliminando…' : 'Eliminar'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </section>
    </AdminLayout>
  );
}
